import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Line, ScrollContainer } from '@eneco/custom-components/Graph';
import { Stack } from '@eneco/sparky';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { useTooltip } from '@visx/tooltip';


import { AxisBottom, AxisRight } from './subcomponents/Axes/Axes';
import { DataCanvas } from './subcomponents/DataCanvas';
import { GridLines } from './subcomponents/GridLines';
import { Tooltip, TooltipData } from './subcomponents/Tooltip/Tooltip';
import { useDynamicPricingTariffsOptional } from '../../hooks/useDynamicPricingTariffs';
import { graphColors } from '../../utils/constants';

const graphHeight = 450;
const graphSpacingBottom = 75;

export const Graph: FC = () => {
  const [innerWidth, setInnerWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const context = useDynamicPricingTariffsOptional();

  const [hasAnchoredTooltip, setHasAnchoredTooltip] = useState(false);
  const { showTooltip, hideTooltip, tooltipOpen, tooltipData } = useTooltip<TooltipData>();

  const data = context?.data;
  const showTotal = context?.showTotal ?? true;

  useEffect(() => {
    if (ref.current) {
      setInnerWidth(ref.current.clientWidth);
    }
  }, [ref.current?.clientWidth]);

  const innerHeight = graphHeight - graphSpacingBottom;

  const xScale = useMemo(
    () =>
      scaleBand({
        domain: data?.map(item => item.start),
        range: [0, innerWidth],
        padding: 1,
        paddingInner: 1,
      }),
    [data, innerWidth],
  );

  // Create a scale from the lowest value to the highest value
  // with an extra margin for min (in case of a negative value) and max
  // or set to default values if no data is available
  const isEmptyDataArray = data?.every(item => Number.isNaN(showTotal ? item.total : item.wholesale));
  const yMin = Math.min(0, ...(data ?? []).map(item => (showTotal ? item.total : item.wholesale) || 0));
  const yMax = !isEmptyDataArray
    ? Math.max(...(data ?? []).map(item => (showTotal ? item.total : item.wholesale) || 0))
    : 0.9;
  const yScaleMin = yMin < 0 ? yMin - (yMax - yMin) * 0.1 : 0;
  const yScaleHeadroom = showTotal || yMin >= 0 ? 0.5 : 1;
  const yScaleMax = yMax + yMax * yScaleHeadroom;

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [yScaleMin, yScaleMax],
        nice: true,
        range: [innerHeight, 0],
      }),
    [yScaleMin, yScaleMax, innerHeight],
  );

  const closeToolTip = () => {
    setHasAnchoredTooltip(false);
    hideTooltip();
  };
  useEffect(() => {
    /**
     * Close tooltip when clicking outside the graph
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        closeToolTip();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const commonProps = { width: innerWidth, height: innerHeight, yScale, xScale, yMax };

  return (
    <Stack direction="row">
      <ScrollContainer
        ref={ref}
        onMouseLeave={() => {
          if (!hasAnchoredTooltip) {
            hideTooltip();
          }
        }}>
        <svg aria-hidden width={innerWidth} height={graphHeight}>
          <Group>
            <GridLines showOriginLine={yScaleMin < 0} width={innerWidth} height={graphHeight} yScale={yScale} />
            {tooltipData && (
              <Line
                from={{ x: tooltipData?.x, y: yScale(yScaleMin) + 25 }}
                to={{ x: tooltipData?.x, y: yScale(0) }}
                stroke={graphColors['backgroundDark']}
                strokeWidth={2}
              />
            )}
            <AxisBottom {...commonProps} selectedValue={tooltipData?.x} />
          </Group>

          <DataCanvas
            onHover={(tooltipData: TooltipData) => {
              if (!hasAnchoredTooltip) {
                showTooltip({
                  tooltipData,
                });
              }
            }}
            {...commonProps}
          />
        </svg>
        <Tooltip
          onClose={closeToolTip}
          data={tooltipData}
          isOpen={tooltipOpen}
          canvasHeight={innerHeight}
          canvasWidth={innerWidth}
        />
      </ScrollContainer>
      <AxisRight yScale={yScale} />
    </Stack>
  );
};
